import React, { useContext, useEffect, useState } from "react";
import { ProcessContext } from "../../../../../contexts/ProcessContext";
import SignatureEmbeddedSAML from "./SignatureEmbeddedSAML";
import SignatureEmbedded from "./SignatureEmbedded";
import SignatureUrlRedirect from "./SignatureUrlRedirect";
import { SIGNATURE_INTEGRATION_TYPES } from "../config";
import { Box } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { MainCircularProgress } from "../../../../shared/components/MainCircularProgress";
import { ACTION_EXCEPTIONS } from "../../../../../utils/getActionException";
import { LOAN_APPLICATION_STATE } from "../../../../../config/config";

const SIGNATURE_REQUEST_EXPIRATION_TIME = 60000 * 10;

export default function Signature({ onSuccess, onOmit }) {
  const {
    state: {
      loan,
      lastActionsReferenceLastChange,
      lastActionsReference,
      signatureChannel,
      actionException,
    },
    dispatch,
  } = useContext(ProcessContext);

  const { palette } = useTheme();

  const [state, setState] = useState({
    showSignatureModule: false,
    signatureChannelChecked: false,
  });

  const { showSignatureModule, signatureChannelChecked } = state;

  const isContractSigned =
    loan?.loan_application_selected?.loan_lender_borrower_contract?.is_signed;

  useEffect(() => {
    dispatch({
      type: "REQUEST_GET_LOAN",
      payload: loan.id,
    });
  }, []);

  useEffect(() => {
    const isActionTypeInLastActionsReference = (actionType) =>
      lastActionsReference?.some((element) => element.type === actionType);
    if (showSignatureModule) return;
    switch (true) {
      case isActionTypeInLastActionsReference("REQUEST_GET_LOAN_FULFILLED"):
        if (!signatureChannelChecked) {
          if (isContractSigned) {
            onOmit();
            return;
          }
          checkSignatureByLoan();
        }
        break;
      case isActionTypeInLastActionsReference(
        "REQUEST_GET_SIGNATURE_URL_BY_LOAN_ID_FULFILLED"
      ):
        onSignatureChannelReceived();
        break;
      case isActionTypeInLastActionsReference(
        "REQUEST_GET_SIGNATURE_URL_BY_LOAN_ID_REJECTED"
      ):
        onSignatureChannelRequestRejected();
        break;
      default:
        break;
    }
  }, [lastActionsReferenceLastChange]);

  const checkSignatureSuccess = () => {
    if (isContractSigned) {
      onSuccess();
    }
  };

  const onSignatureChannelRequestRejected = () => {
    const hasCommunicationWithLenderFailed =
      loan.loan_application_selected?.lender_fields.lender_request_id ===
        null &&
      loan.loan_application_selected?.lender_fields.loan_application_status
        .lender_approval_status === null &&
      loan.loan_application_selected?.lender_fields.loan_application_status
        .state === LOAN_APPLICATION_STATE.SELECTED;

    if (
      actionException ===
        ACTION_EXCEPTIONS.INVALID_LOAN_STATUS_FOR_SIGNATURE_CREATION_EXCEPTION ||
      ACTION_EXCEPTIONS.INVALID_DOWN_PAYMENT_CHECKOUT_SESSION_STATUS_FOR_SIGNATURE_CREATION_EXCEPTION ||
      hasCommunicationWithLenderFailed
    ) {
      onOmit();
      return;
    }
  };

  const onSignatureChannelReceived = () => {
    checkSignatureSuccess();
    setSignatureChannelChecked(true);
    checkSkippingConditions();
  };

  const checkSignatureByLoan = () => {
    const signatureRequestHasExpired =
      Date.now() >
      signatureChannel?.requestedAt + SIGNATURE_REQUEST_EXPIRATION_TIME;
    if (!signatureChannel?.url || signatureRequestHasExpired) {
      dispatch({
        type: "REQUEST_GET_SIGNATURE_URL_BY_LOAN_ID",
        payload: loan.id,
      });
      return;
    }
    onSignatureChannelReceived();
  };

  const checkSkippingConditions = () => {
    if (
      signatureChannel.integration_type === SIGNATURE_INTEGRATION_TYPES.ASYNC
    ) {
      onOmit();
      return;
    }
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: { show: false },
    });
    setState((s) => ({
      ...s,
      showSignatureModule: true,
    }));
  };

  const setSignatureChannelChecked = (value) => {
    setState((s) => ({
      ...s,
      signatureChannelChecked: value,
    }));
  };

  return showSignatureModule ? (
    <>
      {signatureChannel.integration_type ===
        SIGNATURE_INTEGRATION_TYPES.EMBEDDED_SAML && (
        <SignatureEmbeddedSAML onSuccess={onSuccess} />
      )}
      {signatureChannel.integration_type ===
        SIGNATURE_INTEGRATION_TYPES.EMBEDDED && (
        <SignatureEmbedded onSuccess={onSuccess} />
      )}
      {signatureChannel.integration_type ===
        SIGNATURE_INTEGRATION_TYPES.REDIRECT && <SignatureUrlRedirect />}
    </>
  ) : (
    <>
      <Box></Box>
      <MainCircularProgress
        color={palette.secondary.main}
        textId={"INFO_SYNC"}
      />
      <Box></Box>
    </>
  );
}
